import { useEffect, useState } from 'react';
import { useData } from '../contexts/DataContext';
import type { SupportLiveMessage, SupportLiveSession } from '../core/models/SupportLive';

/**
 * Real-time session status + messages for a single supportChats/{id} doc.
 * Mock adapter emits null / [] (live handoff UI stays inert in demo).
 */
export function useSupportLiveSession(
  houseId: string,
  sessionId: string | null,
  enabled = true,
) {
  const { adapter } = useData();
  const [session, setSession] = useState<SupportLiveSession | null>(null);
  const [messages, setMessages] = useState<SupportLiveMessage[]>([]);

  useEffect(() => {
    if (!enabled || !houseId || !sessionId) {
      setSession(null);
      return undefined;
    }
    if (!adapter.subscribeSupportChat) {
      setSession(null);
      return undefined;
    }
    return adapter.subscribeSupportChat(houseId, sessionId, (doc) => {
      setSession(doc ? { id: String(doc.id ?? sessionId), ...doc } as SupportLiveSession : null);
    });
  }, [enabled, houseId, sessionId, adapter]);

  useEffect(() => {
    if (!enabled || !houseId || !sessionId) {
      setMessages([]);
      return undefined;
    }
    if (!adapter.subscribeSupportMessages) {
      setMessages([]);
      return undefined;
    }
    return adapter.subscribeSupportMessages(houseId, sessionId, (rows) => {
      setMessages(rows as unknown as SupportLiveMessage[]);
    });
  }, [enabled, houseId, sessionId, adapter]);

  return { session, messages };
}
